/**
 * The two per-tab flags that carry a cross-tab auth fact across a full page
 * load. A leaf module on purpose: it imports only the slot primitive and the
 * shared TTL, so the auth hooks can stamp a flag
 * without pulling CrossTabAuthSync's graph (BroadcastChannel, Web Locks,
 * analytics) in with it. That is also why `index.ts` does not re-export these.
 *
 * Both are `true`-or-absent and spent by the caller that acts on them, with
 * `clear()`, never by the read:
 *
 * - `signedOutByOtherTabSlot`: stamped by `useCrossTabAuthSync` just before the
 *   reload a sibling's sign-out forces. AuthProvider is its one reader and its
 *   one spender (`useSlotAtMountAndClear`: read at render, cleared in the mount
 *   effect) and hands the captured boolean to everyone else through
 *   `useAuth().signedOutByOtherTabAtMount`; `SignedOutByOtherTabDrawer` opens
 *   from it. Nothing else may read this slot: a second reader would have to win
 *   a race against the spender's clear, and child effects commit before the
 *   parent's.
 * - `userInitiatedSignInSlot`: stamped when the visitor starts a sign-in and read
 *   by AuthProvider once the session exists, to tell "just signed in" from "tab
 *   loaded with tokens already present" before broadcasting to other tabs. The
 *   per-tab key survives the Logto round trip because that hop is a full-page
 *   navigation inside one tab.
 */
import { AUTH_FLOW_TTL_MS } from '@utils/authFlowTtl';
import { defineSessionSlot, isTrue, type SessionSlot } from '@utils/sessionSlot';

const SIGNED_OUT_BY_OTHER_TAB_KEY = 'meetball_signed_out_by_other_tab';
export const USER_INITIATED_SIGNIN_KEY = 'meetball_user_initiated_signin';

export const signedOutByOtherTabSlot: SessionSlot<true> = defineSessionSlot({
  key: SIGNED_OUT_BY_OTHER_TAB_KEY,
  ttlMs: AUTH_FLOW_TTL_MS,
  isValue: isTrue,
});

export const userInitiatedSignInSlot: SessionSlot<true> = defineSessionSlot({
  key: USER_INITIATED_SIGNIN_KEY,
  ttlMs: AUTH_FLOW_TTL_MS,
  isValue: isTrue,
});
